import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import {
  Building2,
  ChevronRight,
  ExternalLink,
  Play,
  Users,
  Workflow,
} from 'lucide-react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { McpSvg } from '@/assets/img/custom/mcp';
import SidebarLayout, { SidebarItem } from '@/app/components/sidebar-layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { AdminClient, AdminClientStatus } from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import SyncClientDialog from '@/features/internal-admin/components/sync-client-dialog';
import { ActiveFlowsUsage } from '@/features/usage/components/active-flows-usage';
import { TasksUsage } from '@/features/usage/components/tasks-usage';

export type ClientDetailContext = {
  tenantId: string;
};

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();

  const {
    data: client,
    isLoading,
    isError,
  } = useQuery<AdminClient, Error>({
    queryKey: ['admin-client', clientId],
    queryFn: () => adminApi.getClient(clientId!),
    enabled: !!clientId,
  });

  const { data: usage, isLoading: isUsageLoading } = useQuery({
    queryKey: ['admin-client-usage', clientId],
    queryFn: () => adminApi.getClientUsage(clientId!),
    enabled: !!clientId,
    refetchInterval: 180000,
  });

  const { data: policy, isLoading: isPolicyLoading } = useQuery({
    queryKey: ['admin-client-policy', clientId],
    queryFn: () => adminApi.getClientPolicy(clientId!),
    enabled: !!clientId,
    staleTime: 60 * 60 * 1000,
  });

  if (isLoading) {
    return <LoadingScreen mode="container" />;
  }

  if (isError || !client) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('Client not found')}</p>
      </div>
    );
  }

  const basePath = `/internal-admin/clients/${clientId}`;

  const navItems: SidebarItem[] = [
    {
      title: t('Users'),
      href: `${basePath}/users`,
      icon: <Users size={20} />,
    },
    {
      title: t('Flows'),
      href: `${basePath}/flows`,
      icon: <Workflow size={20} />,
    },
    {
      title: t('Executions'),
      href: `${basePath}/executions`,
      icon: <Play size={20} />,
    },
    {
      title: t('MCPs'),
      href: `${basePath}/mcps`,
      icon: <McpSvg width={20} height={20} />,
    },
  ];

  const titleElement = (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate('/internal-admin/overview/clients')}
      >
        <Building2 className="h-5 w-5" />
      </Button>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <h2 className="font-semibold tracking-tight">
        {client.companyName ?? client.name}
      </h2>
    </div>
  );

  const clientCard = (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{t('Name')}</p>
            <p className="text-sm font-medium truncate">
              {client.companyName ?? client.name}
            </p>
          </div>
          {client.globalCode && (
            <div className="shrink-0">
              <p className="text-xs text-muted-foreground">
                {t('Company code')}
              </p>
              <p className="text-sm font-medium">{client.globalCode}</p>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          {client.externalCustomerId && (
            <div className="shrink-0">
              <p className="text-xs text-muted-foreground">{t('Client ID')}</p>
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium">
                  {client.externalCustomerId}
                </p>
                <CopyButton
                  textToCopy={client.externalCustomerId}
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 shrink-0"
                />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
          {client.externalCustomerId && (
            <a
              href={`${
                import.meta.env.VITE_KIZEO_OAUTH_URL
              }/admin/customer_details/${client.externalCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('Kizeo client sheet')}
              </Button>
            </a>
          )}
          <Badge
            variant="outline"
            className={cn(
              'w-fit text-sm px-3 py-1 ml-auto',
              client.status === AdminClientStatus.ACTIVE
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-red-100 text-red-700 border-red-200',
            )}
          >
            {client.status === AdminClientStatus.ACTIVE
              ? t('Active')
              : t('Inactive')}
          </Badge>
        </div>
        <div className="mt-3 pt-3 border-t space-y-3">
          {isUsageLoading || isPolicyLoading ? (
            <Skeleton className="h-[52px] w-full" />
          ) : (
            usage &&
            policy &&
            policy.tier !== 'no_access' && (
              <>
                <ActiveFlowsUsage usage={usage} policy={policy} />
                <TasksUsage usage={usage} policy={policy} />
              </>
            )
          )}
        </div>
        {client.externalCustomerId && (
          <SyncClientDialog clientId={client.id} />
        )}
      </CardContent>
    </Card>
  );

  return (
    <SidebarLayout
      title={titleElement}
      items={navItems}
      sidebarHeader={clientCard}
    >
      <Outlet context={{ tenantId: client.id }} />
    </SidebarLayout>
  );
};

export default ClientDetailPage;
